import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BulkContainersService } from '../../core/services';
import { IBulkContainerArticle } from '../../core/interfaces/IBulkContainerArticle';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';
import { NotificationService } from '../../../../core/services';
import { CommentsListComponent } from './comments-list/comments-list.component';
import { ArticlesCommentsService } from '../../../articles/core/ArticlesCommentsService';
import { ArticlesService } from '../../../articles/core/ArticlesService';

@Component({
    selector: 'app-single-bulk-container',
    templateUrl: './single-bulk-container.component.html',
    styleUrls: ['./single-bulk-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SingleBulkContainerComponent implements OnInit, OnDestroy {

  @ViewChild('commentsListComponent') commentsListComponent: CommentsListComponent;

  public title: string;
  public parentTitle = 'Großgebinde';
  public parentLink = '/bulk-containers/overview';
  private _subs: Subscription[] = [];
  articleId$ = new BehaviorSubject<number>(null);
  shopwareArticleId$ = new BehaviorSubject<number>(null);
  bulkContainer$: Observable<IBulkContainerArticle> = null;
  public articleName: string;
  public showCommentControl = false;
  public newComment: string;

  constructor(
    private _bulkContainerService: BulkContainersService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _cdr: ChangeDetectorRef,
    private _notificationService: NotificationService,
    private _articlesCommentsService: ArticlesCommentsService,
    private _articlesService: ArticlesService,
  ) { }

  ngOnInit(): void {
      this._subs.push(
        this._route.params.subscribe(params => {
          this.articleId$.next(+params['id']);
        }),
        this._route.queryParams.subscribe(params => {
          if (!params.hasOwnProperty('tab')) {
            this._router.navigateByUrl(this._router.url + '?tab=deliveries');
          }
        })
      );
      this.articleId$.asObservable().subscribe(() => this.loadContent());
  }

  public loadContent(): void {
    this.shopwareArticleId$.next(null);
    this.bulkContainer$ = this._bulkContainerService.getBulkContainerArticleById(this.articleId$.getValue()).pipe(
      map(x => {
        x.minStock = x.minStock != null ? x.minStock / 1000 : x.minStock;

        return x;
      }),
      tap(res => {
        this.articleName = res.productionArticle?.name;
        this.title = this.parentTitle + ': ' + this.articleName;
        this.shopwareArticleId$.next(res.productionArticle?.shopwareArticleId ?? null);
        this._cdr.markForCheck();
      }),
      catchError(err => {
        if (err.request.status === 404) {
          this._router.navigateByUrl('/bulk-containers/overview');
        }

        return observableThrowError(err);
      })
    );
  }

  public saveComment(): void {
    const productionArticleId = this.articleId$.getValue();
    if (!this.newComment) {
      this._notificationService.pushError('Fehler', 'Kommentar darf nicht leer sein.');
    } else if (!productionArticleId) {
      this._notificationService.pushError('Fehler', 'Produktionsartikel fehlt');
    } else {
      this._articlesCommentsService.create({
        articleId: this.shopwareArticleId$.getValue(),
        productionArticleId,
        comment: this.newComment
      }).subscribe({
        next: () => {
          this.loadContent();
          this.newComment = null;
          this.showCommentControl = false;
          this.commentsListComponent.onRefreshList(true);
        }
      });
    }
  }

  public updateMinStock(minStock: number): void {
    const productionArticleId = this.articleId$.getValue();
    if (!productionArticleId) {
      this._notificationService.pushError('Fehler', 'Produktionsartikel fehlt');
      return;
    }
    const grams = minStock == null ? null : minStock * 1000;
    this._articlesService.updateArticleMinStock(
      this.shopwareArticleId$.getValue(),
      null,
      grams,
      productionArticleId
    ).subscribe({
      next: () => this._notificationService.pushSuccess('Erfolgreich', `Der Mindestbestand wurde korrekt angelegt`),
      error: () => this._notificationService.pushError('Fehler', `Fehler beim Speichern des Mindestbestands`),
    });
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }
}
